/**
 * What the board's Preview cell opens.
 *
 * The thumbnail in that cell is 32 pixels of a real file, and until now the
 * only way to see the file was to leave the board for its own page - which
 * costs the board: the filters, the scroll position and the row you were
 * comparing against. So the cell answers "what is this one" where you are, and
 * leaves "act on this one" to the route it still offers in the footer.
 *
 * The row carries a signed thumbnail and nothing else, so the file itself is
 * read on open: `GET /projects/:id/files/:fileId` is what carries a URL for the
 * asset at full size, the poster a document was rendered to, and the type that
 * decides which of them is shown. TanStack caches it per file, so re-opening
 * one already seen paints immediately and re-signs in the background.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { FilePreview, projectFileQuery } from '@/entities/project-file';
import type { BackToState } from '@/shared/lib/back-to';
import { EmptyState } from '@/shared/ui';
import { formatFileSize } from '@/shared/utils/format';

import { fileTypeIcon } from '../../lib/file-type-icon';

export interface RecordPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The project the file's routes are scoped to - the platform id, not the
   * internal one, which 403s against them. Null while no row is open.
   */
  platformProjectId: string | null;
  /** The file being previewed; null while no row is open. */
  fileId: string | null;
  /**
   * The row's own name and type, so the header says which file is loading
   * rather than appearing once the request lands. A dialog with no accessible
   * name is a dialog a screen reader cannot announce, and this one has to be
   * named before anything is fetched.
   */
  fileName: string;
  fileType: string;
  /** The asset's page, or null when the board cannot build the route. */
  href: string | null;
  /** Where that page's Back control should return to; see `shared/lib/back-to`. */
  backTo?: BackToState;
}

export function RecordPreviewModal(props: RecordPreviewModalProps) {
  const { t } = useTranslation('trackers');
  const { open, onOpenChange, platformProjectId, fileId, fileName, fileType, href, backTo } = props;

  // The modal is mounted for the life of the board, so most renders have no row
  // and nothing to ask for. A row missing the project its file's routes are
  // scoped to is the other case: there is no request to make, and a disabled
  // query reports `pending` forever, so the frame below answers it rather than
  // spinning at someone.
  const canRead = Boolean(platformProjectId) && Boolean(fileId);
  const fileQuery = useQuery({
    ...projectFileQuery(platformProjectId ?? undefined, fileId ?? undefined),
    enabled: open && canRead,
  });
  const file = fileQuery.data;

  const Icon = fileTypeIcon(file?.fileType ?? fileType);
  // One shape for "nothing to show": a file whose type has no preview, and one
  // whose poster will not load. Both leave the person with the file's identity
  // and the way to open it, which is the footer.
  const nothingToShow = (
    <EmptyState
      size="compact"
      icon={<Icon />}
      title={t('trackersBoard.recordPreviewModal.noPreview')}
      description={t('trackersBoard.recordPreviewModal.noPreviewDescription')}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        className="flex h-[80vh] w-[min(calc(100vw-2rem),56rem)] flex-col gap-0 overflow-hidden p-0"
      >
        <DialogHeader className="shrink-0 border-b border-border p-4 pr-14 text-left">
          <DialogTitle className="truncate text-base font-medium">{fileName}</DialogTitle>
          <DialogDescription className="truncate text-xs">
            {file
              ? t('trackersBoard.recordPreviewModal.typeAndSize', {
                  type: file.fileType,
                  size: formatFileSize(file.fileSize),
                })
              : fileType}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted p-4 dark:bg-background">
          {!canRead ? (
            nothingToShow
          ) : fileQuery.isPending ? (
            <Skeleton className="h-full w-full rounded-lg" />
          ) : fileQuery.isError || !file ? (
            <EmptyState
              role="alert"
              size="compact"
              title={t('trackersBoard.recordPreviewModal.couldNotLoad')}
              description={t('trackersBoard.recordPreviewModal.couldNotLoadDescription')}
            >
              <Button variant="outline" size="sm" onClick={() => void fileQuery.refetch()}>
                {t('trackersBoard.recordPreviewModal.tryAgain')}
              </Button>
            </EmptyState>
          ) : (
            <FilePreview file={file} fallback={nothingToShow} />
          )}
        </div>

        {href ? (
          <DialogFooter className="shrink-0 border-t border-border p-4">
            <Button asChild variant="outline" size="sm">
              <Link to={href} state={backTo}>
                {t('trackersBoard.recordPreviewModal.openAsset')}
              </Link>
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
